import React, { useState, useEffect } from 'react';
import apiService, { errorHelpers, formatHelpers, authHelpers } from '../../../utils/apiService';

const ActivityLogs = () => {
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [filters, setFilters] = useState({
        user_id: '',
        activity_type: '',
        date_from: '',
        date_to: '',
        search: ''
    });

    // Users list for filter dropdown
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Get current user for role-based access control
    const currentUser = authHelpers.getCurrentUser();
    const isSuperAdmin = authHelpers.isSuperAdmin();
    const isAdmin = authHelpers.isAdmin();

    useEffect(() => {
        loadActivityLogs();
        loadUsers();
    }, [currentPage]);

    useEffect(() => {
        // Reset to page 1 when filters change
        if (currentPage !== 1) {
            setCurrentPage(1);
        } else {
            loadActivityLogs();
        }
    }, [filters]);

    const loadActivityLogs = async () => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page: currentPage,
                per_page: 50,
                ...filters
            };

            // Role-based filtering
            if (isAdmin && !isSuperAdmin) {
                // Admin can only see:
                // 1. Their own logs
                // 2. Logs from regular users (role = 'user')
                // This is handled on the backend, but we can add a hint here
                params.admin_view = true; // Backend can use this flag
            }

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await apiService.admin.getActivityLogs(params);

            // Additional client-side filtering for admins (backup security)
            let filteredLogs = response.data.logs;

            if (isAdmin && !isSuperAdmin) {
                filteredLogs = response.data.logs.filter(log => {
                    // Show logs where:
                    // 1. User is the current admin
                    // 2. User is a regular user (not another admin/super_admin)
                    // 3. System logs (user_id is null)
                    if (!log.user_id) return true; // System logs
                    if (log.user_id === currentUser.id) return true; // Own logs

                    // Check if the log belongs to a regular user
                    const logUser = users.find(u => u.id === log.user_id);
                    return logUser && logUser.role === 'user';
                });
            }

            setLogs(filteredLogs);
            setPagination(response.data.pagination);

        } catch (err) {
            console.error('Error loading activity logs:', err);
            setError(errorHelpers.getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            const response = await apiService.admin.getUsers({ per_page: 100 });

            // Filter users based on role
            let filteredUsers = response.data.users;

            if (isAdmin && !isSuperAdmin) {
                // Admin can only see regular users (not other admins or super_admins)
                filteredUsers = response.data.users.filter(user => user.role === 'user');
            }
            // Super admin sees all users (no filtering needed)

            setUsers(filteredUsers);
        } catch (err) {
            console.error('Error loading users:', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            user_id: '',
            activity_type: '',
            date_from: '',
            date_to: '',
            search: ''
        });
    };

    const exportToCSV = () => {
        if (!logs.length) {
            alert('No data to export');
            return;
        }

        const headers = [
            'ID',
            'User Email',
            'User Name',
            'Activity',
            'Details',
            'IP Address',
            'Timestamp'
        ];

        const csvRows = [
            headers.join(','),
            ...logs.map(log => [
                log.id,
                log.user_email || 'System',
                log.user_email ? `"${getUserName(log.user_id)}"` : 'System',
                `"${log.activity}"`,
                `"${log.details || 'N/A'}"`,
                log.ip_address || 'N/A',
                formatHelpers.formatDateTime(log.timestamp)
            ].join(','))
        ];

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const getUserName = (userId) => {
        const user = users.find(u => u.id === userId);
        return user ? `${user.firstname} ${user.lastname}` : 'Unknown User';
    };

    const getUserRole = (userId) => {
        const user = users.find(u => u.id === userId);
        return user ? user.role : 'unknown';
    };

    const getActivityIcon = (activity) => {
        const iconMap = {
            'Login': '🔐',
            'Logout': '🚪',
            'Registration': '📝',
            'Password': '🔑',
            'Email': '📧',
            'Profile': '👤',
            'Certificate': '📜',
            'Payment': '💳',
            'Verification': '✅',
            'Admin': '⚙️',
            'Upload': '📤',
            'Download': '📥',
            'Delete': '🗑️',
            'Create': '➕',
            'Update': '✏️',
            'Export': '📊'
        };

        for (const [key, icon] of Object.entries(iconMap)) {
            if (activity.toLowerCase().includes(key.toLowerCase())) {
                return icon;
            }
        }
        return '📋'; // Default icon
    };

    const getActivityTypeOptions = () => {
        const uniqueActivities = [...new Set(logs.map(log => {
            // Extract the main activity type from activity string
            const parts = log.activity.split(' - ');
            return parts[0] || log.activity;
        }))];

        return uniqueActivities.sort();
    };

    const getActivitySeverity = (activity) => {
        const criticalActivities = ['delete', 'disable', 'failed', 'error', 'blocked'];
        const warningActivities = ['password', 'reset', 'verify', 'admin'];
        const infoActivities = ['login', 'view', 'download', 'export'];

        const activityLower = activity.toLowerCase();

        if (criticalActivities.some(keyword => activityLower.includes(keyword))) {
            return 'critical';
        } else if (warningActivities.some(keyword => activityLower.includes(keyword))) {
            return 'warning';
        } else if (infoActivities.some(keyword => activityLower.includes(keyword))) {
            return 'info';
        }
        return 'normal';
    };

    if (loading && logs.length === 0) {
        return (
            <div className="activity-logs">
                <h2>📝 Activity Logs</h2>
                <div className="loading">Loading activity logs...</div>
            </div>
        );
    }

    return (
        <div className="activity-logs">
            <div className="logs-header">
                <div className="header-title">
                    <h2>📝 Activity Logs</h2>
                    {isAdmin && !isSuperAdmin && (
                        <div className="access-notice">
                            <small>👁️ You can view your own logs and user activity logs</small>
                        </div>
                    )}
                    {isSuperAdmin && (
                        <div className="access-notice">
                            <small>👑 Super Admin - Viewing all system logs</small>
                        </div>
                    )}
                </div>
                <div className="header-actions">
                    <button className="btn btn-secondary" onClick={exportToCSV}>
                        📊 Export CSV
                    </button>
                    <button className="btn btn-primary" onClick={loadActivityLogs}>
                        🔄 Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger">
                    ⚠️ {error}
                </div>
            )}

            {/* Filters Section */}
            <div className="filters-section">
                <div className="filters-grid">
                    <div className="filter-group">
                        <label>User:</label>
                        <select
                            value={filters.user_id}
                            onChange={(e) => handleFilterChange('user_id', e.target.value)}
                            disabled={loadingUsers}
                        >
                            <option value="">
                                {isSuperAdmin ? 'All Users' : 'All Accessible Users'}
                            </option>
                            {/* Show current admin's own entry for admins */}
                            {isAdmin && !isSuperAdmin && (
                                <option value={currentUser.id}>
                                    {currentUser.firstname} {currentUser.lastname} (You)
                                </option>
                            )}
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.firstname} {user.lastname} ({user.email})
                                    {user.role !== 'user' && ` - ${user.role}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Activity Type:</label>
                        <select
                            value={filters.activity_type}
                            onChange={(e) => handleFilterChange('activity_type', e.target.value)}
                        >
                            <option value="">All Activities</option>
                            {getActivityTypeOptions().map(activity => (
                                <option key={activity} value={activity}>
                                    {activity}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>From Date:</label>
                        <input
                            type="date"
                            value={filters.date_from}
                            onChange={(e) => handleFilterChange('date_from', e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label>To Date:</label>
                        <input
                            type="date"
                            value={filters.date_to}
                            onChange={(e) => handleFilterChange('date_to', e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Search:</label>
                        <input
                            type="text"
                            placeholder="Search in activities or details..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>

                    <div className="filter-actions">
                        <button className="btn btn-secondary" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics */}
            <div className="logs-stats">
                <div className="stat-item">
                    <span className="stat-label">Total Logs:</span>
                    <span className="stat-value">{formatHelpers.formatNumber(pagination.total || 0)}</span>
                </div>
                <div className="stat-item">
                    <span className="stat-label">Showing:</span>
                    <span className="stat-value">{logs.length} records</span>
                </div>
                <div className="stat-item">
                    <span className="stat-label">Page:</span>
                    <span className="stat-value">{pagination.page || 1} of {pagination.pages || 1}</span>
                </div>
            </div>

            {/* Logs Table */}
            <div className="logs-table-container">
                <table className="logs-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>User</th>
                            <th>Activity</th>
                            <th>Details</th>
                            <th>IP Address</th>
                            <th>Timestamp</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr key={log.id} className={`severity-${getActivitySeverity(log.activity)}`}>
                                <td>{log.id}</td>
                                <td>
                                    <div className="user-info">
                                        {log.user_email ? (
                                            <>
                                                <strong>
                                                    {getUserName(log.user_id)}
                                                    {isAdmin && !isSuperAdmin && log.user_id === currentUser.id && (
                                                        <span className="you-badge"> (You)</span>
                                                    )}
                                                </strong>
                                                <br />
                                                <small>{log.user_email}</small>
                                                {/* Show user role for super admins */}
                                                {isSuperAdmin && (
                                                    <>
                                                        <br />
                                                        <span className={`role-badge role-${getUserRole(log.user_id)}`}>
                                                            {getUserRole(log.user_id)}
                                                        </span>
                                                    </>
                                                )}

                                            </>
                                        ) : (
                                            <span className="system-user">🤖 System</span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="activity-info">
                                        <span className="activity-icon">
                                            {getActivityIcon(log.activity)}
                                        </span>
                                        <span className="activity-text">
                                            {log.activity}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div className="details-cell">
                                        {log.details ? (
                                            <span title={log.details}>
                                                {formatHelpers.truncateText(log.details, 80)}
                                            </span>
                                        ) : (
                                            <span className="no-details">—</span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <code className="ip-address">
                                        {log.ip_address || 'N/A'}
                                    </code>
                                </td>
                                <td>
                                    <div className="timestamp-cell">
                                        <div className="timestamp-main">
                                            {formatHelpers.formatDateTime(log.timestamp)}
                                        </div>
                                        <div className="timestamp-relative">
                                            {getRelativeTime(log.timestamp)}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {logs.length === 0 && !loading && (
                    <div className="no-logs">
                        📝 No activity logs found matching your criteria.
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="pagination">
                    <button
                        className="btn btn-secondary"
                        disabled={!pagination.has_prev}
                        onClick={() => setCurrentPage(currentPage - 1)}
                    >
                        ← Previous
                    </button>

                    <div className="pagination-info">
                        <span>Page {pagination.page} of {pagination.pages}</span>
                    </div>

                    <button
                        className="btn btn-secondary"
                        disabled={!pagination.has_next}
                        onClick={() => setCurrentPage(currentPage + 1)}
                    >
                        Next →
                    </button>
                </div>
            )}

            <style jsx>{`
                .activity-logs {
                    padding: 20px;
                }

                .logs-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 25px;
                }

                .header-title {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }

                .header-title h2 {
                    margin: 0;
                    color: #333;
                }

                .access-notice {
                    margin: 0;
                }

                .access-notice small {
                    color: #6c757d;
                    font-style: italic;
                }

                .logs-header h2 {
                    margin: 0;
                    color: #333;
                }

                .header-actions {
                    display: flex;
                    gap: 10px;
                }

                .alert {
                    padding: 12px 16px;
                    border-radius: 6px;
                    margin-bottom: 20px;
                }

                .alert-danger {
                    background-color: #fee;
                    border: 1px solid #fcc;
                    color: #c33;
                }

                .loading {
                    text-align: center;
                    padding: 40px;
                    color: #666;
                    font-size: 16px;
                }

                .filters-section {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    padding: 20px;
                    margin-bottom: 20px;
                }

                .filters-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 15px;
                    align-items: end;
                }

                .filter-group {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }

                .filter-group label {
                    font-weight: 600;
                    font-size: 14px;
                    color: #333;
                }

                .filter-group input,
                .filter-group select {
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                    background: white;
                }

                .filter-group input:focus,
                .filter-group select:focus {
                    outline: none;
                    border-color: #007bff;
                    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
                }

                .filter-actions {
                    display: flex;
                    align-items: end;
                }

                .logs-stats {
                    display: flex;
                    gap: 30px;
                    margin-bottom: 20px;
                    padding: 15px;
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                }

                .stat-item {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }

                .stat-label {
                    font-weight: 600;
                    color: #666;
                    font-size: 14px;
                }

                .stat-value {
                    font-weight: bold;
                    color: #333;
                    font-size: 14px;
                }

                .logs-table-container {
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    overflow: hidden;
                    margin-bottom: 20px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .logs-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 0;
                }

                .logs-table th,
                .logs-table td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #eee;
                    vertical-align: top;
                }

                .logs-table th {
                    background-color: #f8f9fa;
                    font-weight: 600;
                    color: #333;
                    font-size: 14px;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }

                .logs-table td {
                    font-size: 14px;
                }

                .logs-table tbody tr:hover {
                    background-color: #f8f9fa;
                }

                /* Severity-based row styling */
                .severity-critical {
                    border-left: 4px solid #dc3545;
                    background-color: #fff5f5;
                }

                .severity-warning {
                    border-left: 4px solid #ffc107;
                    background-color: #fffbf0;
                }

                .severity-info {
                    border-left: 4px solid #17a2b8;
                    background-color: #f0f9ff;
                }

                .severity-normal {
                    border-left: 4px solid #28a745;
                    background-color: #f8fff8;
                }

                .user-info strong {
                    display: block;
                    color: #333;
                }

                .user-info small {
                    color: #666;
                    font-size: 12px;
                }

                .system-user {
                    color: #6c757d;
                    font-style: italic;
                    font-weight: 500;
                }

                .you-badge {
                    background-color: #e3f2fd;
                    color: #1976d2;
                    padding: 2px 6px;
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .role-badge {
                    padding: 2px 6px;
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .role-user {
                    background-color: #e8f5e8;
                    color: #2e7d32;
                }

                .role-admin {
                    background-color: #fff3e0;
                    color: #ef6c00;
                }

                .role-super_admin {
                    background-color: #f3e5f5;
                    color: #7b1fa2;
                }

                .role-unknown {
                    background-color: #f5f5f5;
                    color: #616161;
                }

                .activity-info {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .activity-icon {
                    font-size: 16px;
                    flex-shrink: 0;
                }

                .activity-text {
                    font-weight: 500;
                    color: #333;
                }

                .details-cell {
                    max-width: 300px;
                }

                .no-details {
                    color: #999;
                    font-style: italic;
                }

                .ip-address {
                    background-color: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 3px;
                    padding: 2px 6px;
                    font-family: 'Courier New', monospace;
                    font-size: 12px;
                    color: #495057;
                }

                .timestamp-cell {
                    min-width: 160px;
                }

                .timestamp-main {
                    font-weight: 500;
                    color: #333;
                    font-size: 13px;
                }

                .timestamp-relative {
                    font-size: 11px;
                    color: #6c757d;
                    margin-top: 2px;
                }

                .no-logs {
                    text-align: center;
                    padding: 40px;
                    color: #666;
                    font-size: 16px;
                    background: #f8f9fa;
                }

                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 20px;
                    margin-top: 20px;
                    padding: 20px;
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                }

                .pagination-info {
                    font-weight: 500;
                    color: #333;
                }

                .btn {
                    padding: 8px 16px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    background: white;
                    cursor: pointer;
                    font-size: 14px;
                    text-decoration: none;
                    display: inline-block;
                    transition: all 0.2s;
                    font-weight: 500;
                }

                .btn:hover:not(:disabled) {
                    background-color: #f8f9fa;
                    transform: translateY(-1px);
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                .btn-primary {
                    background-color: #007bff;
                    border-color: #007bff;
                    color: white;
                }

                .btn-primary:hover:not(:disabled) {
                    background-color: #0056b3;
                    border-color: #0056b3;
                }

                .btn-secondary {
                    background-color: #6c757d;
                    border-color: #6c757d;
                    color: white;
                }

                .btn-secondary:hover:not(:disabled) {
                    background-color: #545b62;
                    border-color: #545b62;
                }

                @media (max-width: 768px) {
                    .logs-header {
                        flex-direction: column;
                        gap: 15px;
                        align-items: stretch;
                    }

                    .header-actions {
                        justify-content: center;
                    }

                    .filters-grid {
                        grid-template-columns: 1fr;
                    }

                    .logs-stats {
                        flex-direction: column;
                        gap: 10px;
                    }

                    .logs-table-container {
                        overflow-x: auto;
                    }

                    .logs-table {
                        min-width: 800px;
                    }

                    .pagination {
                        flex-direction: column;
                        gap: 15px;
                    }
                }

                @media (max-width: 480px) {
                    .activity-logs {
                        padding: 10px;
                    }

                    .logs-table th,
                    .logs-table td {
                        padding: 8px;
                        font-size: 12px;
                    }
                }
            `}</style>
        </div>
    );

    // Helper function to get relative time
    function getRelativeTime(timestamp) {
        const now = new Date();
        const logTime = new Date(timestamp);
        const diffMs = now - logTime;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatHelpers.formatDate(timestamp);
    }
};

export default ActivityLogs;